/**
 * Performance helpers every tab shares, copied from the design (Aithora ConsoleV3.dc.html):
 * the page tab style (tab(), L18947), the percentile colours (pc(), L18960), the ordinal
 * (ord(), L33401), the grade bands (gradeOf, L35098), the portfolio heat palette (heatA,
 * L33418) and the sixteen CMS columns in the design's order, keyed to the scorer's
 * metric keys (src/financial/benchmarks.ts BENCH_METRICS).
 */
import type { ApiClient, ApiError, BenchMetricKey, PeerTier } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

export const pre = (e?: { preventDefault?: () => void }) => e?.preventDefault?.();

export function usePerfApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}
export const notServed = (e: ApiError | null | undefined) => !!e && e.kind === 'not_found';

/** A page tab (design tab()). */
export const tabStyle = (on: boolean) =>
  'display:inline-block;white-space:nowrap;padding:11px 2px 12px;font-size:16px;font-weight:' + (on ? '700' : '500') +
  ';color:' + (on ? '#2F5C97' : '#54606C') + ';text-decoration:none;border-bottom:2.5px solid ' + (on ? '#2F5C97' : 'transparent') + ';margin-bottom:-1px;';

/**
 * A benchmark metric's percentile as the page prints it. BenchMetric.percentile is 0..1
 * on the wire (the scorer's percentileRank, which Flutter reads too); every reader on
 * this page works in 0..100, so convert here and nowhere else.
 */
/**
 * Operating margin in percent. The catalog stores the HCRIS cost-report margin as a fraction
 * (0.037 is 3.7%; the server's own fmtPct and scoring read it so); a value past 1 already is a
 * percent. Absent stays null: never 0, never ranked.
 */
export const marginPct = (v: number | null | undefined): number | null =>
  v == null || !Number.isFinite(v) ? null : Math.abs(v) <= 1 ? v * 100 : v;
export const pct100 = (p: number | null | undefined): number | null => (p == null || !Number.isFinite(p) ? null : Math.round(p * 100));

/** A percentile's colour (design pc()). */
export const pc = (v: number) => (v >= 75 ? '#1F6B52' : v >= 50 ? '#2F5C97' : v >= 40 ? '#5B6B7C' : '#B3261E');

/** 1st, 2nd, 3rd, 22nd (design ord()). */
export function ord(v: number): string {
  const r10 = v % 10;
  const r100 = v % 100;
  const suf = r10 === 1 && r100 !== 11 ? 'st' : r10 === 2 && r100 !== 12 ? 'nd' : r10 === 3 && r100 !== 13 ? 'rd' : 'th';
  return v + suf;
}

/** The grade band a composite falls in (design gradeOf). The server's own grade wins where it sends one. */
export const gradeOf = (c: number) => (c >= 80 ? 'A' : c >= 72 ? 'B' : c >= 62 ? 'C' : c >= 52 ? 'D' : 'F');
export const GRADE_STYLE: Record<string, [string, string]> = {
  A: ['#1F6B52', 'rgba(47,138,111,.17)'],
  B: ['#2F5C97', 'rgba(79,138,214,.17)'],
  C: ['#5B6B7C', 'rgba(91,107,124,.17)'],
  D: ['#B3261E', 'rgba(179,38,30,.17)'],
  F: ['#B3261E', 'rgba(179,38,30,.24)'],
};

/** The portfolio heat palette: one hue, five steps, the number always printed (design heatA). */
export function heatA(v: number | null): { v: string; bg: string; fg: string } {
  if (v === null) return { v: '-', bg: 'rgba(34,39,46,.05)', fg: '#54606C' };
  if (v >= 90) return { v: String(v), bg: '#1E3F6E', fg: '#FFFFFF' };
  if (v >= 75) return { v: String(v), bg: '#2F5C97', fg: '#FFFFFF' };
  if (v >= 60) return { v: String(v), bg: '#9DB4D3', fg: '#22272E' };
  if (v >= 40) return { v: String(v), bg: '#D3DEEC', fg: '#22272E' };
  return { v: String(v), bg: '#F1F4F8', fg: '#B3261E' };
}

/** The sixteen columns in the design's order (design cols, L35065), with the scorer's key. */
export const COLS: { label: string; key: BenchMetricKey }[] = [
  { label: 'Overall', key: 'overallRating' },
  { label: 'Quality', key: 'qmRating' },
  { label: 'Staffing', key: 'staffingRating' },
  { label: 'Total HPRD', key: 'totalHprd' },
  { label: 'RN HPRD', key: 'rnHprd' },
  { label: 'Nurse turn.', key: 'nurseTurnover' },
  { label: 'RN turn.', key: 'rnTurnover' },
  { label: 'Health insp.', key: 'healthInspectionRating' },
  { label: 'Survey', key: 'healthSurveyScore' },
  { label: 'Penalties', key: 'numPenalties' },
  { label: 'Fines', key: 'finesDollars' },
  { label: 'Severe defic.', key: 'severeDeficiencies' },
  { label: 'Infect. defic.', key: 'infectionDeficiencies' },
  { label: 'Occupancy', key: 'occupancy' },
  { label: 'Op. margin', key: 'operatingMargin' },
  { label: 'Rev / bed', key: 'revenuePerBed' },
];

/** The page's tier labels and their query values. */
export const TIERS: { label: 'City' | 'Region' | 'State' | 'National'; tier: PeerTier }[] = [
  { label: 'City', tier: 'city' },
  { label: 'Region', tier: 'region' },
  { label: 'State', tier: 'state' },
  { label: 'National', tier: 'national' },
];
